"use client";

import { useMemo } from "react";

/**
 * Application Process 1030 — BDU Admission Process.
 *
 * Port of BDU v1 `components/Degree/AdmissionProcess/`: centred heading with a
 * gradient rule, then a wrapping row of numbered step cards.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here.
 *
 * The reference's `admissionProcess.css` centres the card contents at
 * `max-width: 740px` — NOT at the 768px `md:` boundary — so between 741px and 767px
 * the cards are left-aligned while still using the mobile widths. That off-by-28px
 * band is reproduced with `max-[740px]:` rather than being tidied to `tab:`.
 *
 * `application_process` has no `metadata` column, so the steps live in `metadata_1`.
 */

interface StepRow {
  id?: string | number;
  meta_title?: string;
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ApplicationProcess1030Data {
  metadata_1?: StepRow[];
  title?: string;
  title_start_color?: string;
  /** Band behind the whole section */
  desktop_background_color?: string;
  /** Rule under the heading */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Step number ring + digit */
  accent_color?: string;
  status?: boolean;
}

const ApplicationProcess1030 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as
    | ApplicationProcess1030Data
    | undefined;

  const {
    title = "Admission process",
    // The reference heading carries NO colour class, so it renders the inherited
    // browser default (#000000) — not the #222023 `text-blue-175` used by the
    // Featured Programmes and About headings.
    title_start_color = "#000000",
    desktop_background_color = "#FFFFFF",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    accent_color = "#000000",
  } = sectionData || {};

  const steps = useMemo(() => {
    const rows = sectionData?.metadata_1;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata_1]);

  if (!sectionData?.status || steps.length === 0) return null;

  return (
    // `w-full` is not in the reference: there the section is a block element and is
    // 100% wide implicitly. Here the page wraps widgets in a `flex flex-col
    // items-center` <main>, so without it the section sizes to fit-content — which
    // min-content pushes to 407px (289px step card + 118px padding), overflowing a
    // 375px viewport and giving the heading enough room to stop wrapping.
    <div
      className="flex min-h-[407px] w-full flex-col items-center gap-[42px] px-[59px] pb-[77px] pt-[39px] tab:pb-[57px] tab:pt-[33px]"
      style={{ backgroundColor: desktop_background_color || "#FFFFFF" }}
    >
      <div className="flex flex-col items-center justify-center gap-[8px] tab:gap-[20px]">
        <h2
          className="text-center text-[28px] font-bold tab:leading-[100%]"
          style={{ color: title_start_color }}
        >
          {title}
        </h2>
        <div
          className="h-[2px] w-[200px] tab:h-[3px] tab:w-[270px]"
          style={{
            backgroundImage: `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`,
          }}
        />
      </div>

      <div className="flex min-h-[205px] w-full flex-wrap justify-center gap-[36px] tab:gap-[21px]">
        {steps.map((step, index) => (
          <div
            key={step.id ?? index}
            className="flex h-full w-[289px] flex-col gap-[31px] max-[740px]:items-center tab:w-[240px] tab:gap-[14px]"
          >
            <span
              className="flex min-h-[31px] w-[31px] items-center justify-center rounded-[50%] border"
              style={{ borderColor: accent_color, color: accent_color }}
            >
              {index + 1}
            </span>
            <div className="flex flex-col items-center justify-center tab:items-start tab:gap-[10px]">
              <p className="text-lg font-semibold">{step.meta_title}</p>
              <p className="w-[260px] text-sm/[21px] font-normal max-[740px]:text-center tab:w-[194px]">
                {step.meta_description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApplicationProcess1030;
